import React, { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate, Navigate } from "react-router-dom";
import { Lock, EnvelopeSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

function formatErr(d) {
  if (!d) return "Something went wrong.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map(e => e?.msg || JSON.stringify(e)).join(" ");
  if (d.msg) return d.msg;
  return String(d);
}

export default function Login() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      nav("/");
    } catch (e2) {
      setErr(formatErr(e2.response?.data?.detail) || e2.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f4f7fb] flex items-stretch">
      {/* Left panel — brand */}
      <div className="hidden lg:flex flex-col justify-between w-1/2 bg-[#0f1b4c] text-white p-12 relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern" />
        <div className="absolute -top-32 -left-32 w-[500px] h-[500px] aurora rounded-full opacity-70" />

        <div className="relative flex items-center gap-3">
          <img src="/jespay-logo.webp" alt="Jespay" className="h-16 w-auto" style={{filter:"brightness(1.05)"}}/>
        </div>

        <div className="relative">
          <div className="text-[11px] tracking-[0.3em] uppercase text-[#4adb78] mb-4">Financial Office Tracker</div>
          <h1 className="font-display font-black text-5xl xl:text-6xl leading-[0.95]">
            Every rupee.<br/>Every entry.<br/>Every day.
          </h1>
          <p className="mt-6 text-white/70 max-w-md">
            Your payments, simplified. The internal ledger for tracking cash, UPI, DTT & ATM withdrawals — replacing the Excel sheet with something auditable and instant.
          </p>
        </div>

        <div className="relative flex gap-6 font-mono-fin text-xs uppercase tracking-widest text-white/60">
          <span>Cash · UPI · DTT</span>
          <span>Bank-wise</span>
          <span>Real-time balance</span>
        </div>
      </div>

      {/* Right panel: form */}
      <div className="flex-1 flex items-center justify-center p-6">
        <form onSubmit={submit} className="brut-card-lg w-full max-w-md p-8" data-testid="login-form">
          <div className="flex items-center gap-3 mb-6 lg:hidden">
            <img src="/jespay-logo.webp" alt="Jespay" className="h-12 w-auto" />
          </div>

          <div className="text-[11px] tracking-[0.3em] uppercase text-[#2cb4a6] mb-2">Sign in</div>
          <h2 className="font-display font-black text-3xl leading-tight mb-6">
            Welcome to the office ledger.
          </h2>

          <label className="brut-label block mb-2">Email</label>
          <div className="relative mb-4">
            <EnvelopeSimple size={18} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4a5578]" />
            <input
              data-testid="login-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@jespay.com"
              className="brut-input pl-10"
            />
          </div>

          <label className="brut-label block mb-2">Password</label>
          <div className="relative mb-4">
            <Lock size={18} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4a5578]" />
            <input
              data-testid="login-password"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="brut-input pl-10"
            />
          </div>

          {err && (
            <div data-testid="login-error" className="mb-4 border-2 border-[#dc2626] bg-[#fee2e2] p-3 text-sm font-bold text-[#7f1d1d]">
              {err}
            </div>
          )}

          <button
            data-testid="login-submit"
            disabled={busy}
            className="brut-btn brut-btn-primary w-full text-base"
            type="submit"
          >
            {busy ? "Signing in…" : "Sign in →"}
          </button>

          <div className="mt-6 text-xs text-[#4a5578]">
            Staff accounts are created by the admin. Contact your finance admin for access.
          </div>
        </form>
      </div>
    </div>
  );
}
